import React, { useEffect, useState } from 'react';
import {
	Drawer,
	Form,
	Space,
	Switch,
	Table,
	Typography,
} from 'antd';
import {
	Currencies,
	getPortActionTypeLabel,
	PortActionTypes,
} from '@shared/utils/constants';
import { asyncMap } from '@shared/utils/array';
import { formatCurrency } from '@shared/utils/currency';
import { formatNumber } from '@shared/utils/formatNumber';
import { Values } from '@shared/utils/objectEnums';
import { GetVoyageExpensesResponse } from '@api/features/invoices/getVoyageExpenses';
import { UkEtsRow } from '@api/features/ops/utils/emissionsCompliance/emissionsComplianceTypes';
import { updateVoyageExpense } from '@client/lib/api';
import EstimatedActualTag from '@client/components/EstimatedActualTag';
import useFileUpload, { Upload } from '@client/utils/hooks/useFileUpload';
import getFileFromAttachment from '@client/utils/getFileFromAttachment';
import CurrencyInput from '@client/components/CurrencyInput';
import LoadingIndicator from '@client/components/LoadingIndicator';
import Button from '@client/components/Button';
import NumericInput from '@client/components/NumericInput';
import InvoiceAttachment from '../InvoiceAttachment';
import styles from './UkEtsExpenseDrawer.module.css';

type Props = {
	open: boolean;
	onClose: () => void;
	expenseData?: Partial<GetVoyageExpensesResponse['rows'][number]> | null;
};

type SavedSnapshot = {
	overrideAmount: number | null;
	ukaActualPrice: number | null;
	rows: UkEtsRow[];
	attachments: Array<{
		name: string;
		attachmentId?: number;
	}>;
};

type UploadedAttachmentFile = File & {
	uid: string;
	attachmentId?: number;
};

const serializeSnapshot = (snapshot: SavedSnapshot) => JSON.stringify(snapshot);

const renderConsumptionSource = (
	source?: 'actual' | 'estimated',
) => {
	if (source == null) {
		return '—';
	}

	return (
		<EstimatedActualTag
			estimated={source === 'estimated'}
		/>
	);
};

const calculateRows = (
	rows: UkEtsRow[],
	ukaActualPrice: number | null,
): UkEtsRow[] => rows.map((row) => {
	const ukasRequired = row.apply ?
		row.totalCo2 * (row.qualifyingPercent / 100) :
		0;

	const amount =
		ukasRequired * (ukaActualPrice ?? 0);

	return {
		...row,
		ukasRequired,
		amount,
		contractAmount: amount,
	};
});

const UkEtsExpenseDrawer = ({
	open,
	onClose,
	expenseData,
}: Props) => {
	const [loadingAttachments, setLoadingAttachments] = useState(false);

	const {
		uploaded,
		uploadProps,
		onRemoveUpload,
		onSetUploaded,
		onResetUploaded,
	} = useFileUpload({ multiple: true });
	
	const hasBreakdown =
		(expenseData?.ukCalculationRows?.length ?? 0) > 0;

	const [ukaActualPrice, setUkaActualPrice] = useState<number | null>(
		expenseData?.ukaActualPrice ?? null,
	);

	const [overrideAmount, setOverrideAmount] = useState<number | null>(
		expenseData?.overrideAmount == null ?
			null :
			Number(expenseData.overrideAmount),
	);

	const [saving, setSaving] = useState(false);
	const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);

	const [localRows, setLocalRows] = useState<UkEtsRow[]>(
		(expenseData?.ukCalculationRows ?? []) as UkEtsRow[],
	);

	const currency = expenseData?.currency ?? Currencies.USD;

	const hasResolvableUkaPrice =
		ukaActualPrice != null &&
		Number.isFinite(ukaActualPrice) &&
		ukaActualPrice > 0;

	const calculatedRows = calculateRows(
		localRows,
		ukaActualPrice,
	);

	const totalCo2 = calculatedRows.reduce(
		(sum, row) => sum + (row.apply ? row.totalCo2 : 0),
		0,
	);

	const totalUkas = calculatedRows.reduce(
		(sum, row) => sum + row.ukasRequired,
		0,
	);

	const totalAmount = calculatedRows.reduce(
		(sum, row) => sum + row.amount,
		0,
	);

	useEffect(() => {
		setOverrideAmount(
			expenseData?.overrideAmount == null ?
				null :
				Number(expenseData.overrideAmount),
		);

		setUkaActualPrice(
			expenseData?.ukaActualPrice ?? null,
		);

		setLocalRows(
			(expenseData?.ukCalculationRows ?? []) as UkEtsRow[],
		);
	}, [
		expenseData?.id,
		expenseData?.overrideAmount,
		expenseData?.ukaActualPrice,
		expenseData?.ukCalculationRows,
	]);

	useEffect(() => {
		const updateDefaultFiles = async () => {
			setLoadingAttachments(true);

			if (
				expenseData == null ||
				expenseData.attachments?.length === 0
			) {
				setLoadingAttachments(false);

				return;
			}

			const expenseAttachments = expenseData.attachments ?? [];

			const expenseFiles = await asyncMap(
				expenseAttachments,
				async (attachment) => {
					const file: File & {
						uid?: string;
						attachmentId?: number;
						deleteable?: boolean;
					} = await getFileFromAttachment(attachment);

					file.uid = `${file.name}-${file.lastModified}`;
					file.attachmentId = attachment.id;

					return {
						file: file as File & {
							uid: string;
							attachmentId: number;
							deleteable?: boolean;
						},
						url: window.URL.createObjectURL(file),
						deleteable: true,
					};
				},
			);

			onSetUploaded(expenseFiles);
			setLoadingAttachments(false);
		};

		updateDefaultFiles();
	}, [expenseData, onSetUploaded]);

	useEffect(() => {
		if (expenseData == null || loadingAttachments) {
			return;
		}

		const snapshot: SavedSnapshot = {
			overrideAmount:
			expenseData.overrideAmount == null ?
				null :
				Number(expenseData.overrideAmount),
			ukaActualPrice:
			expenseData.ukaActualPrice == null ?
				null :
				Number(expenseData.ukaActualPrice),
			rows: calculateRows(
				(expenseData.ukCalculationRows ?? []) as UkEtsRow[],
				expenseData.ukaActualPrice == null ?
					null :
					Number(expenseData.ukaActualPrice),
			),
			attachments: (expenseData.attachments ?? []).map((attachment) => ({
				name: attachment.name,
				attachmentId: attachment.id,
			})),
		};

		setSavedSnapshot(serializeSnapshot(snapshot));
	}, [
		expenseData,
		loadingAttachments,
	]);

	useEffect(() => {
		if (!open) {
			onResetUploaded();
		}
	}, [open, onResetUploaded]);

	const uploadedFiles: Upload[] = [];

	if (Array.isArray(uploaded)) {
		uploadedFiles.push(...uploaded);
	} else if (uploaded != null) {
		uploadedFiles.push(uploaded);
	}

	const currentSnapshot: SavedSnapshot = {
		overrideAmount,
		ukaActualPrice,
		rows: calculatedRows,
		attachments: uploadedFiles.map((upload) => {
			const file = upload.file as UploadedAttachmentFile;

			return {
				name: file.name,
				attachmentId: file.attachmentId,
			};
		}),
	};

	const currentSerializedSnapshot =
		serializeSnapshot(currentSnapshot);

	const hasChanges =
		savedSnapshot != null &&
		currentSerializedSnapshot !== savedSnapshot;

	const onSave = async () => {
		if (
			expenseData?.id == null ||
			(
				overrideAmount == null &&
				hasBreakdown &&
				!hasResolvableUkaPrice
			)
		) {
			return;
		}

		setSaving(true);

		try {
			const amount =
				overrideAmount ?? totalAmount;

			await updateVoyageExpense(expenseData.id, {
				ukaActualPrice,
				ukCalculationRows: calculatedRows,
				overrideAmount,
				amount: -Math.abs(amount),
				estimated: true,
				attachments: uploadedFiles.map((upload) => upload.file),
			});

			setSavedSnapshot(currentSerializedSnapshot);
			onClose();
		} finally {
			setSaving(false);
		}
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title="UK ETS"
			width="70%"
			destroyOnClose
		>
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				<Form layout="vertical">
					<div className={styles.overrideFields}>
						<Form.Item label="Manual overwrite">
							<div className={styles.overrideRow}>
								<CurrencyInput
									allowClear
									onClear={() => setOverrideAmount(null)}
									currency={currency}
									value={overrideAmount}
									onChange={(value) => {
										setOverrideAmount(
											value == null ? null : Number(value),
										);
									}}
								/>

								{overrideAmount == null && (
									<Typography.Text
										type="secondary"
										className={styles.overrideInfo}
									>
										You can manually overwrite the calculated cost here
									</Typography.Text>
								)}
							</div>
						</Form.Item>
					</div>

					<div className={styles.calculatedTotal}>
						<Form.Item label="Calculated total">
							<CurrencyInput
								currency={currency}
								value={Number(Math.abs(totalAmount).toFixed(2))}
								readOnly
								disabled={overrideAmount != null}
							/>
						</Form.Item>
					</div>

					{hasBreakdown && (
						<div className={styles.priceFields}>
							<Form.Item label="UKA actual price">
								<NumericInput
									className={styles.actualPriceInput}
									value={ukaActualPrice}
									onChange={(value) => {
										setUkaActualPrice(
											value == null ? null : Number(value),
										);
									}}
									addonBefore={currency}							
								/>
							</Form.Item>
						</div>
					)}
				</Form>
				{hasBreakdown && (
					<Table
						rowKey="key"
						dataSource={calculatedRows}
						pagination={false}
						size="small"
						rowClassName={(row) => (
							row.totalCo2 === 0 || row.qualifyingPercent === 0 ?
								styles.disabledRow :
								''
						)}
						summary={() => (
							<Table.Summary.Row>
								<Table.Summary.Cell index={0}>
									<strong>Total</strong>
								</Table.Summary.Cell>

								<Table.Summary.Cell index={1} />

								<Table.Summary.Cell index={2} />

								<Table.Summary.Cell index={3} align="right">
									<strong>
										{formatNumber(totalCo2, undefined, 2)}
									</strong>
								</Table.Summary.Cell>

								<Table.Summary.Cell index={4} />

								<Table.Summary.Cell index={5} />

								<Table.Summary.Cell index={6} align="right">
									<strong>
										{formatNumber(totalUkas, undefined, 2)}
									</strong>
								</Table.Summary.Cell>

								<Table.Summary.Cell index={7} align="right">
									<strong>
										{formatCurrency(totalAmount, currency)}
									</strong>
								</Table.Summary.Cell>
							</Table.Summary.Row>
						)}
						columns={[
							{
								title: 'Port / Passage',
								dataIndex: 'label',
								key: 'label',
							},
							{
								title: 'Purpose',
								dataIndex: 'purpose',
								render: (purpose?: string) => {
									if (!purpose) {
										return '';
									}

									return purpose
										.split(', ')
										.map((p) => getPortActionTypeLabel(
											p as Values<typeof PortActionTypes>,
										))
										.join(', ');
								},
							},
							{
								title: 'Source',
								dataIndex: 'consumptionSource',
								key: 'consumptionSource',
								align: 'center',
								render: renderConsumptionSource,
							},
							{
								title: 'Total CO₂',
								dataIndex: 'totalCo2',
								key: 'totalCo2',
								align: 'right',
								render: (value: number) => formatNumber(value, undefined, 2),
							},
							{
								title: 'Apply',
								dataIndex: 'apply',
								key: 'apply',
								align: 'center',
								render: (apply: boolean, row: UkEtsRow) => (
									<Switch
										disabled={
											row.qualifyingPercent === 0 || 
										row.totalCo2 === 0
										}
										checked={
											apply &&
										row.qualifyingPercent > 0 &&
										row.totalCo2 > 0
										}
										onChange={(checked) => {
											setLocalRows((rows) => rows.map((item) => (
												item.key === row.key ?
													{
														...item,
														apply: checked,
													} :
													item
											)));
										}}
									/>
								),
							},
							{
								title: 'Qualifying %',
								dataIndex: 'qualifyingPercent',
								key: 'qualifyingPercent',
								align: 'right',
								render: (value: number) => `${formatNumber(value, undefined, 2)}%`,
							},
							{
								title: 'UKAs required',
								dataIndex: 'ukasRequired',
								key: 'ukasRequired',
								align: 'right',
								render: (value: number) => formatNumber(value, undefined, 2),
							},
							{
								title: 'Contract amount',
								dataIndex: 'contractAmount',
								key: 'contractAmount',
								align: 'right',
								render: (value: number) => formatCurrency(value, currency),
							},
						]}
					/>
				)}
				<Typography.Title level={5}>
					Attachments
				</Typography.Title>

				{loadingAttachments ? (
					<LoadingIndicator />
				) : (
					<div className={styles.attachmentsInner}>
						<InvoiceAttachment
							files={uploaded}
							uploadProps={uploadProps}
							onRemoveFile={onRemoveUpload}
						/>
					</div>
				)}

				<Space style={{ justifyContent: 'flex-end', width: '100%' }}>
					<Button onClick={onClose}>
						Close
					</Button>

					<Button
						type="primary"
						onClick={onSave}
						loading={saving}
						disabled={
							!hasChanges ||
							(
								overrideAmount == null &&
								hasBreakdown &&
								!hasResolvableUkaPrice
							)
						}
					>
						Save Changes
					</Button>
				</Space>
			</Space>
		</Drawer>
	);
};

export default UkEtsExpenseDrawer;